// Others
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/store';
import { SummarySelector, getDisplayableTitles, titlesWithinBounds } from './SummarySelector';
import { SummaryLevelSelector } from './SummaryLevelSelector';
import BoxActionsPanel from '../common/BoxActionsPanel';
import BoxFormControlsPortal from '../RightSide/BoxFormControlsPortal';

// Common
import { isATitleBox } from '../../utils/boxes';
import { usePreviewContext } from '../../contexts/PreviewContext';
import { renderTextWithEmoji } from '../../webviewer/utils/renderDraftContent';
import { summaryIndexStyle, summaryLeaderStyle, summarySkipLineMargin } from './summaryStyles';

const selector =
  ({ id, startFrom, duppedBreakIdx }) =>
    ({ boxes, masks, columns, summaryConfiguration, configuration, selectedBoxId }) => {
      const box = [...boxes, ...masks.map((m) => m.boxes).flat()].find((box) => box.id === id);
      const allTitles = boxes.filter((box) => isATitleBox(box.type));
      const displayableTitles = getDisplayableTitles(boxes, summaryConfiguration, configuration, columns, masks, box.content.configuration.visibleLevels);
      const { lineSummaryStart, lineSummaryEnd } = box.content.configuration;
      const isSelected = selectedBoxId === id;

      const titlesToDisplay = titlesWithinBounds({ lineSummaryStart, lineSummaryEnd, displayableTitles, allTitles, startFrom, duppedBreakIdx });
      return {
        titles: titlesToDisplay,
        summaryConfiguration: summaryConfiguration,
        configuration,
        isSelected,
      };
    };

const itemSelector =
  () =>
    ({ gridConfiguration }) => ({ gridConfiguration });

// `pageNumber` : faux numéro de page de l'aperçu des paramètres. Le document ne
// le passe jamais, le PDF remplit lui-même la colonne.
// `indentation` : retrait en px de chaque niveau par rapport au précédent.
export const SummaryTitleItem = ({ title, style, boxId, isFirst, isLast, titleConfig, indexStyleFromTitles, pageNumber, indentation = 15 }) => {
  const margin = (Number(title.type[title.type?.length - 1]) - 1) * indentation;
  // Les marges du niveau espacent les lignes : elles vont sur la ligne seule.
  // Recopiées sur ses morceaux, elles décaleraient le texte du titre, mais pas
  // le numéro ni le n° de page, posés en absolu.
  const { marginTop, marginBottom, ...textStyle } = style;
  const fontColor = style.color?.style;
  const showPageNumber = style.showPageNumber !== false;
  const leaderStyle = summaryLeaderStyle(style, fontColor);
  // Style du numéro quand il suit celui des titres du document, sinon vide :
  // le numéro garde alors celui de sa ligne.
  const indexStyle = summaryIndexStyle({ titleConfig, fromTitles: indexStyleFromTitles });

  const { gridConfiguration } = useStore(
    itemSelector()
  );

  return (
    <>
      {/* Le PDF pose sur cette ligne le lien vers le titre. */}
      <div data-summary-title-id={title.id} style={{
        display: 'flex', justifyContent: 'space-between',
        marginTop: isFirst ? 0 : marginTop,
        // La ligne sautée après ce niveau s'ajoute à sa marge basse. Pas de
        // blanc en fin de sommaire : la dernière ligne n'en prend aucune.
        marginBottom: isLast ? 0 : (marginBottom || 0) + summarySkipLineMargin(style)
      }}>
        {/* Numéro et texte en deux colonnes : un titre sur plusieurs lignes se
            cale sous son texte, pas sous le numéro. minWidth 0 + overflowWrap :
            un mot plus long que la ligne se coupe au lieu de sortir de la page. */}
        <span style={{
          position: 'relative',
          display: 'flex',
          minWidth: 0
        }}>
          {/* Copie cachée : elle seule réserve la largeur de la colonne des
              numéros, en Space Mono pour que tous les chiffres l'occupent
              pareil. Elle ne prend pas le style du numéro : en chasse fixe la
              graisse ne change pas les largeurs, la réserve resterait la même.
              Un numéro gras mord donc un peu sur l'écart qui suit. */}
          <span
            style={{
              ...textStyle,
              color: fontColor,
              marginLeft: margin,
              flexGrow: 1,
              fontFamily: 'Space Mono',
              paddingRight: gridConfiguration.summary.paddingSummary,
              flexShrink: 0,
              visibility: 'hidden'
            }}
          >
            {title.indexTitle}
          </span>
          <span
            style={{
              ...textStyle,
              color: fontColor,
              ...indexStyle,
              marginLeft: margin,
              position: 'absolute',
              left: 0,
              marginTop: 'auto',
              marginBottom: 'auto'

            }}
          >
            {title.indexTitle}
          </span>
          <span style={{
            ...textStyle,
            color: fontColor,
            flexGrow: 1,
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
          >
            {/* En texte brut, un emoji de titre sortait invisible du PDF. */}
            {renderTextWithEmoji(title.text)}
          </span>
        </span>
        {leaderStyle && <span style={leaderStyle} />}
        {/* Sans cet id, le PDF n'a pas où écrire le numéro : la colonne reste
            vide pour ce niveau. */}
        {showPageNumber && <span style={{
          position: 'relative'
        }}
          id={`${boxId}-${title.id}`}
        >
          <span
            style={{
              ...textStyle, color: fontColor,
              fontFamily: 'Space Mono',
              visibility: 'hidden'
            }}
          >
            {pageNumber}
          </span>
          <span
            style={{
              ...textStyle, color: fontColor,
              position: 'absolute',
              right: 0,
              marginTop: 'auto',
              marginBottom: 'auto'
            }}
          >
            {pageNumber}
          </span>
        </span>}
      </div>
    </>
  );
};

const Summary = ({ box }) => {
  const { t } = useTranslation();
  const { startFrom, duppedBreakIdx } = usePreviewContext();
  const { titles, summaryConfiguration, configuration, isSelected } = useStore(selector({ id: box.id, startFrom, duppedBreakIdx }));

  // Section libellée plutôt qu'une rangée de sélecteurs nus : les deux listes
  // de titres (début / fin) sont identiques à l'œil, rien ne disait laquelle
  // bornait quoi.
  const boxControls = isSelected && (
    <>
      <BoxFormControlsPortal title={t('summary')}>
        <SummaryLevelSelector selectedBox={box} />
        <SummarySelector selectedBox={box} />
      </BoxFormControlsPortal>
      <BoxActionsPanel />
    </>
  );

  if (titles.length === 0) {
    return (
      <>
        <div
          style={{
            width: box.width,
            backgroundColor: 'lightgray',
            padding: '1rem',
            color: 'black'

          }}
        >
          <p style={{ margin: 0 }}>Sommaire - Aucun titre dans le document</p>
        </div>
        {boxControls}
      </>
    )
  }

  return (
    <>
      <div
        style={{
          width: box.width,
        }}
        className='summary-wrapper'
      >
        {titles.map((title, idx) => (
          <SummaryTitleItem
            key={title.id}
            boxId={box.id}
            title={title}
            style={summaryConfiguration[title.type]}
            isFirst={idx === 0}
            isLast={idx === titles.length - 1}
            titleConfig={configuration[title.type]}
            indexStyleFromTitles={summaryConfiguration.indexStyleFromTitles}
            indentation={summaryConfiguration.indentation}
          />
        ))}
      </div>
      {boxControls}
    </>
  );
};

export default Summary;
